import { Injectable } from '@angular/core';
import { TermsApiService } from '@tangilla/tg-ng-core';
import { Model } from 'survey-core';
import * as Survey from 'survey-core';
import { CivicDataUtilsService } from './civic-data.utils.service';
import { RequestParameters } from '@tangilla/tg-ng-models';
import { firstValueFrom } from 'rxjs';
import { MappedFieldsService } from './mapped-fields.service'
import { ErrorsService } from './errors.service';
import { VariablesService } from '../survey/variables.service';

interface CommitteeTerm {
  term_id: string;
  committee_name: string;
  label: string;
  committee_type: string;
  application_received?: boolean;
  committee_description?: string;
  positions?: any[];
  term_start_date?: string;
  term_end_date?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CommitteeUtilsService {
  isLoading = false;

  constructor(
    private termsApiService: TermsApiService,
    private civicDataUtilsService: CivicDataUtilsService,
    private mappedFieldsService: MappedFieldsService,
    public errorsService: ErrorsService,
    private variablesService: VariablesService
  ) {
  }

  async loadCommitteeTerms(personId: any, model: Model) {
    this.isLoading = true;
    const mappedField = this.mappedFieldsService.getMappedFields(model);
    await this.getCommitteeTerms(personId, model, mappedField);
    this.isLoading = false;
  }

  async getCommitteeTerms(personId: any, model: Model, mapped: any) {

    if (personId){      
      let params = new RequestParameters('open_for_applications=true', undefined, undefined, undefined, 'label', 'ASC');       
      params = params.setAdditionalFields({ available_positions_only: true, person_id: personId });
      
      try{
        const committeeTermsData = await firstValueFrom(this.termsApiService.get(params));
        this.processCommitteeTerms(committeeTermsData, model, mapped)
      }
      catch(error){
        this.errorsService.processError(error);
      }
    }
  }

  private processCommitteeTerms(payload: any, model: Model, mapped: any) {
    const committeeTerms = payload;
    if (mapped.CommitteeApplications.question === '') return;

    const question = model.getQuestionByName(mapped.CommitteeApplications.question);    
    
    this.clearQuestionChoices(question);
    this.populateCommitteeChoices(question, committeeTerms);
    this.setupValueChangeHandler(model, mapped, committeeTerms);
  }

  private clearQuestionChoices(question: any): void {
    question['choices'].splice(0, question['choices'].length);
  }

  private populateCommitteeChoices(question: any, committeeTerms: any) {    
    for(const term of committeeTerms.items){
      const termDescription = this.createTermDescription(term);      
      const choice = new Survey.ItemValue(term.term_id, termDescription);
      
      choice['html'] = termDescription;
      if(term.application_received){
        choice.enableIf = 'false'
      }
      
      question['choices'].push(choice);
    }
  }

  private createTermDescription(term: CommitteeTerm): string {
    if (term.application_received) {
      return `<div class="committee-option received">
                <strong>${term.committee_name}</strong> 
                <span>Your application has been received.</span>
              </div>`;
    }

    const committeeDescription = term.committee_description?.replace(/\n/g, '<br />') || '';
    return `<div class="committee-option">
              <strong>${term.committee_name}</strong> 
              <div class='choice-term'>
                <i class='fa fa-user-tie fa-sm'></i> ${term.positions?.length || 0} position/s available 
                <i class='fa fa-calendar fa-sm'></i> Serving from ${term.term_start_date} to ${term.term_end_date}
              </div>
              <div class='choice-description'>${committeeDescription}</div>
            </div>`;
   }

  private setupValueChangeHandler(model: Model, mapped: any, committeeTerms: any): void {
    model.onValueChanged.add((survey: Model, options: any) => {
      if (options.name === mapped.CommitteeApplications.question) {
        this.handleCommitteeSelection(survey, options, committeeTerms);
      }
      if (options.name === mapped.home_street_1.question) {
        this.handleAddressChange(survey, mapped);
      }
    });
  }

  private handleCommitteeSelection(survey: Model, options: any, committeeTerms: any): void {
    const term = committeeTerms.items.find((commiteeTerm: CommitteeTerm) => commiteeTerm.term_id === options.value);
    if (!term) return;

    if (survey.getQuestionByName('CommitteeName')) {
      survey.setValue('CommitteeName', `${term.committee_name} (${term.label})`);
    }
    if (survey.getQuestionByName('CommitteeType')) {
      survey.setValue('CommitteeType', term.committee_type);
    }
  }

  private handleAddressChange(survey: Model, mapped: any): void {
    if (!survey.getQuestionByName('CivicInformation')) return;

    const that = this;
    setTimeout(() => {
      const address = this.buildAddressString(survey, mapped);
      that.civicDataUtilsService.getCivicData(address, survey);
    }, 1000);
  }

  private buildAddressString(survey: Model, mapped: any): string {
    return `${survey.getValue(mapped.home_street_1.question)} ${survey.getValue(mapped.home_city.question)} ${survey.getValue(mapped.home_state.question)} ${survey.getValue(mapped.home_zip.question)}`;
  }
}
